import { z } from 'zod';
import { UploadAccountDocumentMetadataSchema } from './account-document.js';
import { LowercaseUuidSchema, PaginationQuerySchema, createPaginatedResponseSchema } from './common.js';
/**
 * Every kind of record a document can be linked to. A string column, not a
 * database enum, so a new value needs no migration.
 */
export const DocumentEntityTypeSchema = z.enum([
    'ACCOUNT',
    'CLIENT',
    'BANK_STATEMENT',
    'BANK_STATEMENT_LINE',
    'SUPPLIER_INVOICE',
    'SUPPLIER_INVOICE_EXTRACTION',
    'CONVERSATION',
    'LEDGER_ENTRY_SET',
    'ASSET',
    'ACTIVITY',
]);
/** One record of the account. For `ACCOUNT` the id is the account's own id. */
export const DocumentRecordRefSchema = z.strictObject({
    entityType: DocumentEntityTypeSchema,
    entityId: LowercaseUuidSchema,
});
export const CreateDocumentLinkInputSchema = z.strictObject({
    /** The record the caller reached the document through. */
    from: DocumentRecordRefSchema,
    to: DocumentRecordRefSchema,
    /** Only for a link to `ACCOUNT`; required there when the document has no type yet. */
    filing: UploadAccountDocumentMetadataSchema.omit({ idempotencyKey: true }).optional(),
    idempotencyKey: z.string().min(1).max(255, 'Idempotency key must be 255 characters or less'),
});
/** A document's link to one record; `archivedAt` is set once the link is removed. */
export const DocumentLinkSchema = z.object({
    id: z.string(),
    documentId: z.string(),
    entityType: DocumentEntityTypeSchema,
    entityId: z.string(),
    createdAt: z.iso.datetime(),
    archivedAt: z.iso.datetime().nullable(),
});
export const DocumentLinkListResponseSchema = z.object({
    links: z.array(DocumentLinkSchema),
});
/** A live document linked to no record: it stays with the account until it is filed or linked again. */
export const UnlinkedDocumentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number(),
    createdAt: z.iso.datetime(),
    /** The account document download, which serves an unlinked document. */
    downloadUrl: z.string(),
});
export const ListUnlinkedDocumentsQuerySchema = z.strictObject({ ...PaginationQuerySchema.shape });
export const UnlinkedDocumentListResponseSchema = createPaginatedResponseSchema(UnlinkedDocumentSchema);
